import type { WorkspaceGrant } from '~/utils/workspaceAccess'
import type { RouteLocationRaw } from 'vue-router'
import * as Sentry from '@sentry/nuxt'

export default function useAuth() {
  const account = useState<components['schemas']['Account'] | null>(
    'auth_account',
    () => null
  )
  const collaborator = useState<components['schemas']['Collaborator'] | null>(
    'auth_collaborator',
    () => null
  )
  const isAuthenticated = computed<boolean>(() => !!account.value)

  const grants = computed(() => assignedGrants(collaborator.value))
  const hasWorkspaceAccess = (grant: WorkspaceGrant | 'admin') =>
    isAdmin() || (grant !== 'admin' && grants.value.has(grant))
  const canUsePhone = computed(() =>
    ['call_tools', 'projects', 'marketing', 'scheduling', 'campaigns'].some(
      (grant) => hasWorkspaceAccess(grant as WorkspaceGrant)
    )
  )

  const isAdmin = () => {
    const { currentOrganization: organization } = useOrganization()
    if (
      organization.value === null ||
      account.value === null ||
      collaborator.value === null
    )
      return false

    if (account.value.is_staff || account.value.is_superuser) return true
    if (organization.value.user.id === account.value.id) return true
    return collaborator.value.is_admin
  }

  const logout = async (to?: RouteLocationRaw) => {
    const { $api, $umami } = useNuxtApp()
    await $api('/account/logout/', {
      method: 'POST',
    }).then(() => {
      account.value = null
      sessionStorage.clear()

      Sentry.setUser(null)
      $umami.identify()

      navigateTo(to ?? { name: 'account-login' })
    })
  }

  const setAccount = async () => {
    const { $api, $umami } = useNuxtApp()
    await $api<components['schemas']['Account']>('/account/')
      .then((response) => {
        account.value = response

        const userPayload = {
          id: response.id,
          email: response.email,
          username: response.email,
        }

        Sentry.setUser(userPayload)
        $umami.identify(userPayload)
      })
      .catch(() => (account.value = null))
  }

  const setCollaborator = async () => {
    const { $api } = useNuxtApp()
    await $api<components['schemas']['Collaborator']>('/collaborator/')
      .then((response) => (collaborator.value = response))
      .catch(() => (collaborator.value = null))
  }

  return {
    account,
    collaborator,
    hasWorkspaceAccess,
    canUsePhone,
    isAdmin,
    isAuthenticated,
    logout,
    setAccount,
    setCollaborator,
  }
}
